<template>
  <BasePage title="Customers" subtitle="Manage customers customer information">
    <template #page-header:actions>
      <v-btn
        color="primary"
        depressed
        v-if="currentUser?.hasPermission('customers.add_customer')"
        @click="openCustomerFormDialog()"
      >
        <template #prepend>
          <v-icon start class="hidden-sm-and-down me-0"> {{ route.meta.icon }} </v-icon>
        </template>
        Create Customer
      </v-btn>
    </template>

    <template #page-header:bottom>
      <ErrorAlert
        v-if="error != null"
        dense
        :error="error"
        @clearErrors="clearErrors"
        class="mb-0 mt-4 rounded"
      />
    </template>

    <v-card flat height="calc(100vh - 286px)" class="px-4 rounded-lg">
      <v-toolbar flat height="84" color="transparent">
        <CustomersSearchBox
          v-model:search="currentSearch"
          @update:search="
            (newSearch: string) =>
              debounceFilterCustomers({ ...customerFilters, page: 1, search: newSearch })
          "
          :filters="customerFilters"
          @update:filters="(newFilter) => updateFilters(customerFilters, newFilter)"
        />
      </v-toolbar>

      <CustomerList
        v-model:page="currentPage"
        :loading="isLoading"
        :customers="customers ?? []"
        :count="count"
        @customer-selected="openCustomerDetailDrawer"
        @customer-create-pressed="openCustomerFormDialog()"
        @customer-edit-pressed="openCustomerFormDialog"
        @customer-delete-pressed="openDeleteCustomerDialog"
        @customer-change-password-pressed="openCustomerChangePasswordDialog"
        @customer-change-sites-pressed="openCustomerSitesDialog"
        @customer-set-active-pressed="openCustomerSetActiveDialog"
      />
    </v-card>

    <ConfirmationDialog
      v-if="selectedCustomer"
      width="500"
      title="Delete customer"
      v-model="customerDeleteDialog"
      v-model:error="deleteError"
    >
      <template #message>
        Are you sure you want to delete
        <span class="text-medium-high-emphasis font-weight-bold">{{
          selectedCustomer.getFullName()
        }}</span
        >?
        <br />
        <div class="pt-2">This action cannot be undone.</div>
      </template>

      <template #actions>
        <v-spacer></v-spacer>
        <v-btn
          color="error"
          variant="flat"
          :disabled="deleteInProgress"
          :loading="deleteInProgress"
          @click="removeCustomer(selectedCustomer!)"
        >
          Delete
        </v-btn>
      </template>
    </ConfirmationDialog>
  </BasePage>

  <CustomerDetailDrawer
    v-if="selectedCustomer"
    v-model:drawer="rightDrawer"
    :customer-id="selectedCustomer.id!"
    @customer-edit-pressed="openCustomerFormDialog"
    @customer-delete-pressed="openDeleteCustomerDialog"
    @customer-change-password-pressed="openCustomerChangePasswordDialog"
    @customer-set-active-pressed="openCustomerSetActiveDialog"
  >
  </CustomerDetailDrawer>

  <CustomerFormDialog
    v-model:dialog="isCustomerFormDialogActive"
    :customer="selectedCustomer!"
    :isEdit="!!selectedCustomer"
    ref="customerFormDialogRef"
  />

  <CustomerDetailSitesFormDialog
    v-if="selectedCustomer"
    :customerId="selectedCustomer.id!"
    v-model:dialog="isCustomerSitesDialogActive"
  />

  <template v-if="selectedCustomer && currentUser?.hasPermission('users.change_password')">
    <UserChangePasswordFormDialog
      v-model:dialog="changePasswordDialog"
      :user="selectedCustomer"
      :isCustomer="true"
    />

    <UserSetActiveFormDialog
      v-model:dialog="setActiveDialog"
      :user="selectedCustomer"
      :isCustomer="true"
    />
  </template>
</template>

<script lang="ts" setup>
import { debounce, merge } from 'lodash'
import { storeToRefs } from 'pinia'
import { computed, reactive, ref, watch } from 'vue'

import type { ICustomerFilterParam } from '@/services'
import type { ICustomer } from '@/models/customer'

import { useRoute, useRouter } from 'vue-router'
import { useQueryClient } from '@tanstack/vue-query'
import { useAuthStore } from '@/stores'
import { useDeleteCustomer, useFetchCustomers } from '@/composables/customer'
import { updateFilters } from '@/utils/filters'
import { DebounceDelay } from '@/utils/constants'

import BasePage from '@/components/base/BasePage.vue'
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue'
import CustomerDetailDrawer from '@/components/customers/CustomerDetailDrawer.vue'
import CustomerDetailSitesFormDialog from '@/components/customers/CustomerDetailSitesFormDialog.vue'
import CustomerFormDialog from '@/components/customers/CustomerFormDialog.vue'
import CustomerList from '@/components/customers/CustomerList.vue'
import ErrorAlert from '@/components/common/ErrorAlert.vue'
import CustomersSearchBox from '@/components/customers/CustomersSearchBox.vue'
import UserChangePasswordFormDialog from '@/components/user/UserChangePasswordFormDialog.vue'
import UserSetActiveFormDialog from '@/components/user/UserSetActiveFormDialog.vue'

const route = useRoute()
const router = useRouter()

const rightDrawer = ref(false)

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

// set default filters from route query param
// only use valid filter fields from routes query params including `search` and `page` and other valid fields
const customerFilters = reactive<ICustomerFilterParam>({
  search: route.query.search as string,
  page: route.query.page ? Number(route.query.page) : 1,
  is_active: route.query.is_active as string
})

//We use a separate ref for the search model in order to not update the queryKey once the search is updated
const currentSearch = ref<string>(customerFilters.search ?? '')

watch(
  customerFilters,
  (value) => {
    /* this will update the URL query based on param values. */
    router
      .replace({
        name: route.name!,
        query: value as Record<string, any>
      })
      .catch((e: Error) => e) //catch navigation duplication error
  },
  {
    immediate: true
  }
)

const currentPage = computed({
  get() {
    return customerFilters.page!
  },
  set(page: number) {
    customerFilters.page = page
  }
})

const selectedCustomer = ref<ICustomer>()

function openCustomerDetailDrawer(customer: ICustomer) {
  selectedCustomer.value = customer

  rightDrawer.value = true
}

const isCustomerFormDialogActive = ref(false)

function openCustomerFormDialog(customer?: ICustomer) {
  selectedCustomer.value = customer

  isCustomerFormDialogActive.value = true
}

const isCustomerSitesDialogActive = ref(false)

function openCustomerSitesDialog(customer: ICustomer) {
  selectedCustomer.value = customer

  isCustomerSitesDialogActive.value = true
}

const customerDeleteDialog = ref(false)

function openDeleteCustomerDialog(customer: ICustomer) {
  selectedCustomer.value = customer
  customerDeleteDialog.value = true
}

function closeDrawerAndDialog() {
  customerDeleteDialog.value = false

  // incase of delete in detail drawer
  rightDrawer.value = false
}

const changePasswordDialog = ref(false)
const setActiveDialog = ref(false)

function openCustomerChangePasswordDialog(customer: ICustomer) {
  selectedCustomer.value = customer

  changePasswordDialog.value = true
}
function openCustomerSetActiveDialog(customer: ICustomer) {
  selectedCustomer.value = customer

  setActiveDialog.value = true
}

// CORE
const queryClient = useQueryClient()

const { customers, count, isLoading, error, queryKey } = useFetchCustomers(customerFilters)

// debouncing
const debounceFilterCustomers = debounce(function (filterParams: ICustomerFilterParam) {
  // mutates filters
  merge(customerFilters, filterParams)
  queryClient.invalidateQueries({ queryKey: queryKey.value })
}, DebounceDelay.Long)

function clearErrors() {
  error.value = null
}
const {
  isPending: deleteInProgress,
  error: deleteError,
  mutate: deleteCustomer
} = useDeleteCustomer()

function removeCustomer(customer: ICustomer) {
  deleteCustomer(customer.id!, {
    onSuccess: () => {
      closeDrawerAndDialog()
    }
  })
}
</script>

<style lang="scss">
.site-list .v-list-item--three-line .v-list-item-subtitle {
  -webkit-line-clamp: 5;
}

.card-list {
  --v-card-list-gap: 0.25rem;

  &.v-list {
    padding-block: 0;
  }

  .v-list-item {
    min-block-size: unset;
    min-block-size: auto !important;
    padding-block: 0 !important;
    padding-inline: 0 !important;

    > .v-ripple__container {
      opacity: 0;
    }

    &:not(:last-child) {
      padding-block-end: var(--v-card-list-gap) !important;
    }
  }

  .v-list-item:hover,
  .v-list-item:focus,
  .v-list-item:active,
  .v-list-item.active {
    > .v-list-item__overlay {
      opacity: 0 !important;
    }
  }
}
</style>
